/* ============================================================
   PDP v2 — gallery enhancement
   ------------------------------------------------------------
   Adds a thumbnail rail and pinch/double-tap zoom to the product
   gallery, using the Swiper 11 already bundled with the theme.

   Runs ONLY when <body class="pdp-v2">, which the layout sets for
   templates/product.pdp-v2.json alone. The default PDP keeps its
   original gallery and never executes any of this.

   Implemented as progressive enhancement rather than an edit to
   main-product.liquid, because that section is shared with the
   default template and must stay byte-identical. Without JS the
   base gallery still works — arrows, swipe, scrollbar.

   ORDER MATTERS: the base instance runs loop:true, which leaves
   duplicate slides in the DOM. Those must be gone before the
   thumbnails are built, or the rail ends up with more cells than
   there are images and the two lists drift out of sync.
   ============================================================ */
(function () {
  'use strict';

  if (!document.body || !document.body.classList.contains('pdp-v2')) return;
  if (typeof Swiper === 'undefined') return;

  var root = document.querySelector('.productVisualSwiper');
  if (!root) return;

  /* main-product.js constructs its own Swiper here. Swiper 11 stores the
     instance on el.swiper, so wait for it rather than racing it. */
  var tries = 0;
  (function waitForBase() {
    if (root.swiper) return enhance(root.swiper);
    if (++tries > 60) return;               // ~3s, then give up quietly
    setTimeout(waitForBase, 50);
  })();

  /* The caffeine meter used to be relocated here with JavaScript. It is now
     a proper block inside main-product, rendered server-side in the buy
     column, so that hack is gone. */

  /* ============================================================
     BUY COLUMN — to design spec
     ------------------------------------------------------------
     Three additions the design has and the theme does not:
       · price inside the Add to Cart button
       · per-cup price under each size, with a BEST VALUE mark
       · the selected size mirrored beside the "Choose size" label

     All done from our own script rather than by editing
     main-product.liquid, because that section is shared with the
     default template. Everything here is additive — nothing the
     theme's own JS depends on is moved or renamed.
     ============================================================ */
  /* The buy column (price in the button, sizes, delivery cards, pills) moved to
     assets/fgo-buy-box.js on 16 Sep 2026 so it runs on every product template. */

  /* ============================================================
     ACCORDION ACCESSIBILITY
     The theme toggles an .active class on the button and animates
     grid-template-rows, which works visually but tells assistive
     tech nothing — there is no aria-expanded, so a screen reader
     user cannot tell open from closed.

     main-product.js is shared with the default template, so rather
     than change its behaviour this mirrors the class into proper
     ARIA state, scoped to our template only.
     ============================================================ */
  (function accordionAria() {
    var buttons = document.querySelectorAll('.productDropdownsItemButton');
    if (!buttons.length) return;

    /* NON-BREAKING SPACES from pasted copy.
       This content is metaobject rich text, authored by pasting from a doc,
       and paste carries U+00A0 with it. One Quality & Testing sentence had
       TWELVE of them, producing an 86-character run containing no ordinary
       space: "Our hibiscus is USDA Organic, certified organic by CCOF, and".
       A run like that has no legal wrap point, so it overflows the card and is
       clipped -- the reader loses the middle of the sentence entirely.

       No CSS fixes this. overflow-wrap: break-word only trades it for a break
       mid-word ("organi / c"), which is what we saw first. The text itself has
       to gain wrap opportunities, so we put them back.

       Scoped to the accordion panels and applied to text nodes only, so no
       markup is touched. U+00A0 is almost always a paste artifact in this
       field; where it is deliberate (holding "8 oz" together) losing it costs
       far less than a clipped sentence. Also fixes any future paste without
       anyone having to notice. */
    (function () {
      var panels = document.querySelectorAll('.productDropdownsItemContentInner');
      for (var i = 0; i < panels.length; i++) {
        var walker = document.createTreeWalker(panels[i], NodeFilter.SHOW_TEXT, null);
        var node;
        while ((node = walker.nextNode())) {
          if (node.nodeValue.indexOf(' ') !== -1) {
            node.nodeValue = node.nodeValue.replace(/ /g, ' ');
          }
        }
      }
    }());

    /* A collapsed panel is hidden by height, not by `display`, so any link
       inside it stays in the tab order. aria-hidden alone would then create
       the worst of both worlds: focus lands on a control the screen reader
       has been told to ignore, and the user hears nothing. `inert` removes
       it from focus AND from the a11y tree, which is what we actually mean. */
    var setPanelState = function (panel, open) {
      if (!panel) return;
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (open) panel.removeAttribute('inert');
      else panel.setAttribute('inert', '');
    };

    buttons.forEach(function (btn, i) {
      var panel = btn.nextElementSibling;
      var open = btn.classList.contains('active');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) {
        if (!panel.id) panel.id = 'pdp-acc-panel-' + i;
        btn.setAttribute('aria-controls', panel.id);
        setPanelState(panel, open);
      }
    });

    /* the theme's own listener flips .active; observe it rather than
       binding a second click handler that could fall out of step */
    var obs = new MutationObserver(function (records) {
      records.forEach(function (r) {
        var btn = r.target;
        if (!btn.classList || !btn.classList.contains('productDropdownsItemButton')) return;
        var open = btn.classList.contains('active');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        setPanelState(btn.nextElementSibling, open);

        /* ONE AT A TIME. Opening a panel closes any other that is open.
           With five panels and a phone viewport, several open at once pushed
           the rest of the page far below the fold and made the group hard to
           scan -- you lose your place in a list you can no longer see the
           shape of.

           Done by removing .active from the others rather than by binding a
           click handler, so the theme's own toggle stays the single source of
           truth and cannot fall out of step with us. No loop risk: clearing
           .active on an already-inactive button changes nothing, so the
           observer that fires for it does no further work. */
        if (open) {
          buttons.forEach(function (other) {
            if (other !== btn && other.classList.contains('active')) {
              other.classList.remove('active');
            }
          });
        }
      });
    });
    buttons.forEach(function (b) {
      obs.observe(b, { attributes: true, attributeFilter: ['class'] });
    });
  })();

  function enhance(base) {
    var wrapper = root.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    /* ---- 1. tear the base instance down first ------------------
       cleanStyles:true strips Swiper's inline transforms. Without it
       the replacement inherits a stale offset and opens on the wrong
       slide. */
    try {
      base.destroy(true, true);
    } catch (e) {
      return;                                /* leave the working gallery alone */
    }

    /* Swiper 11 marks cloned loop slides; older markup may not. Remove
       both forms so the slide list is exactly the real images. */
    wrapper.querySelectorAll('.swiper-slide-duplicate, [data-swiper-slide-index] + [data-swiper-slide-index="0"]')
      .forEach(function (n) {
        if (n.classList.contains('swiper-slide-duplicate')) n.remove();
      });

    var slides = Array.prototype.slice.call(root.querySelectorAll('.productVisualSwiperSlide'));
    if (slides.length < 2) {
      /* nothing to enhance — restore a plain gallery and stop */
      return initPlain();
    }

    /* ---- 2. zoom containers -----------------------------------
       Swiper's zoom module needs each zoomable image wrapped in
       .swiper-zoom-container. Videos are skipped; zooming a playing
       video is not something anyone wants. */
    var zoomable = 0;
    slides.forEach(function (slide) {
      if (slide.querySelector('video')) return;
      var img = slide.querySelector('img');
      if (!img || img.closest('.swiper-zoom-container')) return;
      var box = document.createElement('div');
      box.className = 'swiper-zoom-container';
      img.parentNode.insertBefore(box, img);
      box.appendChild(img);
      zoomable++;
    });

    /* ---- 3. thumbnail rail, built from the real slides --------- */
    var thumbsHost = document.createElement('div');
    thumbsHost.className = 'pdpThumbs swiper';
    var thumbsWrap = document.createElement('div');
    thumbsWrap.className = 'swiper-wrapper';
    thumbsHost.appendChild(thumbsWrap);

    var built = 0;
    slides.forEach(function (slide) {
      var media = slide.querySelector('img, video');
      if (!media) return;
      var cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'pdpThumb swiper-slide';
      cell.setAttribute('aria-label', 'View image ' + (built + 1) + ' of ' + slides.length);
      var thumbImg = document.createElement('img');
      if (media.tagName === 'IMG') {
        var s = media.getAttribute('src') || '';
        /* ask the CDN for a small render instead of scaling the full one */
        /* 112 CSS px at 2dpr = 224 device px; 200 was a soft undershoot */
        thumbImg.src = /[?&]width=\d+/.test(s) ? s.replace(/([?&]width=)\d+/, '$1224') : s;
      }
      thumbImg.alt = '';
      thumbImg.loading = 'lazy';
      cell.appendChild(thumbImg);
      thumbsWrap.appendChild(cell);
      built++;
    });

    if (built < 2) return initPlain();
    root.parentNode.insertBefore(thumbsHost, root.nextSibling);

    /* the rail replaces the scrollbar as the position indicator */
    var scrollbarEl = root.querySelector('.swiper-scrollbar');
    if (scrollbarEl) scrollbarEl.style.display = 'none';

    /* ---- 4. rebuild with thumbs + zoom ------------------------ */
    try {
      /* Vertical beside the stage on desktop, horizontal beneath it on
         smaller screens. Swiper reads `direction` from breakpoints, so
         this survives a resize without a re-init. */
      var thumbsSwiper = new Swiper(thumbsHost, {
        direction: 'horizontal',
        slidesPerView: 'auto',
        spaceBetween: 10,
        watchSlidesProgress: true,
        freeMode: true,
        /* our cells are <button>s with their own aria-labels; Swiper's
           a11y module was stamping role="group" onto them -- invalid on a
           button, and the one failure in the Agentic Browsing 4/4 (23 Aug
           audit). The buttons are the accessible artifact; the module is
           redundant here. */
        a11y: false,
        breakpoints: {
          900: {
            direction: 'vertical',
            spaceBetween: 9
          }
        }
      });

      var opts = {
        slidesPerView: 1,
        spaceBetween: 20,
        autoHeight: true,
        loop: false,                 /* loop clones desync the rail */
        initialSlide: 0,
        navigation: {
          nextEl: root.querySelector('.swiper-button-next'),
          prevEl: root.querySelector('.swiper-button-prev')
        },
        thumbs: { swiper: thumbsSwiper },
        keyboard: { enabled: true, onlyInViewport: true },
        a11y: { enabled: true },
        on: {
          slideChange: function () {
            root.querySelectorAll('.productVisualSwiperSlide video')
              .forEach(function (v) { v.pause(); });
          }
        }
      };
      /* No in-place zoom. A click opens the lightbox instead, and zoom
         lives in there where the canvas is big enough to be worth it.
         Both on the same element would fight: a double-click for zoom
         fires two single clicks, so one gesture would trigger the other. */
      var mainSwiper = new Swiper(root, opts);
      root.classList.add('pdp-gallery-ready');
      if (zoomable) setupLightbox(mainSwiper, slides);
    } catch (e) {
      /* the base is already destroyed, so leaving this half-done would
         strand a dead gallery. Put a working one back. */
      thumbsHost.remove();
      if (scrollbarEl) scrollbarEl.style.display = '';
      initPlain();
    }
  }

  function initPlain() {
    try {
      new Swiper(root, {
        slidesPerView: 1,
        spaceBetween: 20,
        autoHeight: true,
        navigation: {
          nextEl: root.querySelector('.swiper-button-next'),
          prevEl: root.querySelector('.swiper-button-prev')
        }
      });
    } catch (e) { /* nothing further to try */ }
  }

  /* ============================================================
     LIGHTBOX
     Click the stage to open images full-screen with pinch and
     double-tap zoom. Built lazily on first open so it costs
     nothing for the majority who never use it.
     ============================================================ */
  function setupLightbox(mainSwiper, slides) {
    var box = null, lbSwiper = null, lastFocus = null;

    /* an explicit affordance — a cursor change alone doesn't tell
       anyone on a touch screen that the image does something */
    var hint = document.createElement('button');
    hint.type = 'button';
    hint.className = 'pdpExpand';
    hint.setAttribute('aria-label', 'Expand image');
    hint.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M9 4H4v5M15 4h5v5M15 20h5v-5M9 20H4v-5"/></svg>';
    root.appendChild(hint);
    hint.addEventListener('click', function (e) {
      e.stopPropagation();
      open(mainSwiper.activeIndex || 0);
    });

    root.addEventListener('click', function (e) {
      if (e.target.closest('.swiper-button-prev, .swiper-button-next, .pdpExpand')) return;
      if (e.target.closest('video')) return;
      open(mainSwiper.activeIndex || 0);
    });

    function build() {
      box = document.createElement('div');
      box.className = 'pdpLightbox';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', 'Product images');

      var inner = '<button type="button" class="pdpLightboxClose" aria-label="Close">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg></button>' +
        '<div class="pdpLightboxSwiper swiper"><div class="swiper-wrapper">';

      slides.forEach(function (slide) {
        var img = slide.querySelector('img');
        if (!img) return;
        var src = img.getAttribute('src') || '';
        /* full-resolution render for the big canvas; Shopify makes it on demand */
        var big = /[?&]width=\d+/.test(src) ? src.replace(/([?&]width=)\d+/, '$11946') : src;
        inner += '<div class="swiper-slide"><div class="swiper-zoom-container">' +
                 '<img src="' + big + '" alt="' + (img.getAttribute('alt') || '') + '">' +
                 '</div></div>';
      });

      inner += '</div><div class="swiper-button-prev"></div><div class="swiper-button-next"></div>' +
               '</div><p class="pdpLightboxCount" aria-live="polite"></p>';
      box.innerHTML = inner;
      document.body.appendChild(box);

      box.querySelector('.pdpLightboxClose').addEventListener('click', close);
      /* clicking the backdrop closes; clicking the image should not */
      box.addEventListener('click', function (e) {
        if (e.target === box) close();
      });

      var count = box.querySelector('.pdpLightboxCount');
      lbSwiper = new Swiper(box.querySelector('.pdpLightboxSwiper'), {
        slidesPerView: 1,
        zoom: { maxRatio: 3, toggle: true },
        keyboard: { enabled: true },
        a11y: { enabled: true },
        navigation: {
          nextEl: box.querySelector('.pdpLightboxSwiper .swiper-button-next'),
          prevEl: box.querySelector('.pdpLightboxSwiper .swiper-button-prev')
        },
        on: {
          slideChange: function () {
            count.textContent = (this.activeIndex + 1) + ' / ' + this.slides.length;
          }
        }
      });
      count.textContent = '1 / ' + lbSwiper.slides.length;
    }

    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      /* keep focus inside the dialog */
      if (e.key !== 'Tab' || !box) return;
      var f = box.querySelectorAll('button:not([disabled])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function open(index) {
      lastFocus = document.activeElement;
      if (!box) build();
      if (!lbSwiper) return;
      lbSwiper.slideTo(index, 0);
      box.classList.add('is-open');
      document.documentElement.classList.add('pdp-lb-open');   /* scroll lock */
      document.addEventListener('keydown', onKey);
      box.querySelector('.pdpLightboxClose').focus();
    }

    function close() {
      if (!box) return;
      if (lbSwiper && lbSwiper.zoom) lbSwiper.zoom.out();
      box.classList.remove('is-open');
      document.documentElement.classList.remove('pdp-lb-open');
      document.removeEventListener('keydown', onKey);
      /* hand focus back where it came from, not to the top of the page */
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
  }
})();


/* HEADER dock — moved to assets/fgo-header.js so it runs on every
   template, not just the PDP. Do not re-add it here. */


/* ---------------------------------------------------------------------
   Scroll reveal
   ---------------------------------------------------------------------
   One observer for every section that opts in with [data-pdp-reveal],
   rather than one per section. Unobserves on first entry: these are
   arrivals, not a state that should re-trigger every time the user
   scrolls back up — a band that re-animates on the way back reads as a
   bug, not as polish.

   No-JS and old-browser paths both need the content visible, so the
   .is-in class is added rather than removed, and anything unobserved
   simply stays in its final state.
   --------------------------------------------------------------------- */
(function () {
  var nodes = document.querySelectorAll('[data-pdp-reveal]');
  if (!nodes.length) return;

  /* Content must never depend on an animation firing. The hidden-at-rest
     rules are scoped to .js-reveal, which is only added here — so if this
     file fails to parse, the observer is throttled (Chrome does this for
     occluded windows), or IntersectionObserver is missing, every section
     simply renders visible instead of disappearing. */
  document.documentElement.classList.add('js-reveal');

  /* Belt and braces: anything still unrevealed after 3s gets revealed
     anyway. Covers throttling and any intersection edge case. */
  setTimeout(function () {
    for (var f = 0; f < nodes.length; f++) nodes[f].classList.add('is-in');
  }, 3000);

  var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (still || !('IntersectionObserver' in window)) {
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('is-in');
    return;
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      io.unobserve(e.target);
    });
  }, {
    /* fire a little before the band is fully on screen, so the motion is
       finishing as it settles rather than starting once it has arrived */
    rootMargin: '0px 0px -12% 0px',
    threshold: 0.12
  });

  for (var k = 0; k < nodes.length; k++) io.observe(nodes[k]);
}());



/* ---------------------------------------------------------------------
   Origin-story CTA — make it do what it says
   ---------------------------------------------------------------------
   Shipped as <a href="#buy">Add to cart</a>. Nothing on the page has the id
   "buy" — the buy section's real id is template--<n>__buy — so the anchor
   matched nothing and the browser jumped to the top of the document. The
   label also promised an action it was never going to perform.

   Both fixed: the href is repointed at the real section so a no-JS visitor
   still lands somewhere sensible, and the click is handed to the actual Add
   to Cart button, which owns the variant, the selling plan, the loading
   state and the cart drawer. Delegating rather than reimplementing means
   this cannot drift away from the real control.
   --------------------------------------------------------------------- */
(function () {
  if (!document.body || !document.body.classList.contains('pdp-v2')) return;

  var cta = document.querySelector('.pdpOrigCta');
  if (!cta) return;

  var buySection = document.querySelector('section.product');
  var atc = document.querySelector('.productATC');

  /* Repoint the anchor at the buy block's REAL id rather than inventing one.
     An earlier version assigned `buySection.id = 'buy'` to make href="#buy"
     resolve — which overwrote `template--<n>__buy`. Shopify scopes a
     section's entire inline stylesheet to that id, so overwriting it silently
     switched off every rule the section styles itself with, starting with its
     60px top padding. The section id is load-bearing; never reassign it. */
  var wrapper = buySection && buySection.closest('.shopify-section');
  var anchorId = (buySection && buySection.id) ||
                 (wrapper && wrapper.id) || '';
  if (anchorId) cta.setAttribute('href', '#' + anchorId);

  if (!atc) return;

  cta.addEventListener('click', function (e) {
    if (atc.disabled) return;            /* sold out — let the anchor scroll */
    e.preventDefault();
    atc.click();
  });

  /* it behaves as a control now, so announce it as one */
  cta.setAttribute('role', 'button');
}());


/* ---------------------------------------------------------------------
   Gallery flags — first slide only
   ---------------------------------------------------------------------
   .productVisualTags ("Bestseller") is pinned over the stage rather than
   living inside a slide, so it stayed put while the images moved beneath
   it and sat on top of the baked-in headlines on the marketing shots.

   Bound by polling for the instance rather than inside enhance(), because
   the gallery may end up running either the enhanced swiper or the plain
   fallback, and this should hold either way.
   --------------------------------------------------------------------- */
(function () {
  if (!document.body || !document.body.classList.contains('pdp-v2')) return;

  var tags = document.querySelector('.productVisualTags');
  var root = document.querySelector('.productVisualSwiper');
  if (!tags || !root) return;

  var bound = false;
  var sync = function (sw) {
    /* realIndex, not activeIndex — they diverge the moment loop mode is on */
    var i = typeof sw.realIndex === 'number' ? sw.realIndex : sw.activeIndex;
    tags.classList.toggle('is-off', i > 0);
  };

  var tries = 0;
  (function wait() {
    var sw = root.swiper;
    if (sw && !bound) {
      bound = true;
      sw.on('slideChange', function () { sync(sw); });
      sw.on('slideChangeTransitionEnd', function () { sync(sw); });
      sync(sw);
      return;
    }
    if (++tries > 60) return;            /* ~6s, then leave the flag as-is */
    setTimeout(wait, 100);
  }());
}());


/* ---------------------------------------------------------------------
   Recommended carousel — give it a way to be discovered
   ---------------------------------------------------------------------
   The theme builds this Swiper with no navigation, no scrollbar and no
   pagination, so ten products sat behind a half-cropped card at the right
   edge. That crop is the only signal there is more, and a cropped card
   reads as a layout fault rather than as an invitation — a mouse user gets
   nothing to act on at all.

   The buttons call the existing instance's slideNext/slidePrev rather than
   re-initialising it with the navigation module, so the theme's own config
   is left exactly as it ships. If Swiper is missing or the cards already
   fit, nothing is inserted.
   --------------------------------------------------------------------- */
(function () {
  if (!document.body || !document.body.classList.contains('pdp-v2')) return;

  var setup = function () {
    var grids = document.querySelectorAll('[id*="recommended"] .recommendedGrid');

    for (var i = 0; i < grids.length; i++) {
      (function (grid) {
        if (!grid || grid.dataset.pdpNav) return;
        var sw = grid.swiper;
        if (!sw) return;

        /* nothing to navigate to */
        if (sw.isBeginning && sw.isEnd) return;

        grid.dataset.pdpNav = '1';
        grid.parentNode.classList.add('pdpRecViewport');

        var ARROW = function (dir) {
          return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="' +
            (dir === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7') + '"/></svg>';
        };

        var nav = document.createElement('div');
        nav.className = 'pdpRecNav';

        var mk = function (dir, label) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'pdpRecNavBtn';
          b.setAttribute('aria-label', label);
          b.innerHTML = ARROW(dir);
          b.addEventListener('click', function () {
            if (dir === 'prev') sw.slidePrev(); else sw.slideNext();
          });
          return b;
        };

        var prev = mk('prev', 'Previous products');
        var next = mk('next', 'Next products');
        nav.appendChild(prev);
        nav.appendChild(next);
        grid.parentNode.insertBefore(nav, grid);

        var sync = function () {
          prev.disabled = sw.isBeginning;
          next.disabled = sw.isEnd;
          /* the edge fade only means anything while something is hidden */
          grid.parentNode.classList.toggle('has-more', !sw.isEnd);
        };
        sw.on('slideChange', sync);
        sw.on('reachEnd', sync);
        sw.on('reachBeginning', sync);
        sw.on('resize', sync);
        sync();
      }(grids[i]));
    }
  };

  /* the theme builds its Swiper on DOMContentLoaded; wait for the instance
     rather than racing it, same pattern as the gallery above */
  var tries = 0;
  (function wait() {
    setup();
    if (++tries > 40) return;              /* ~4s, then stop quietly */
    setTimeout(wait, 100);
  }());

  document.addEventListener('shopify:section:load', function () {
    setTimeout(setup, 200);
  });
}());


/* ---------------------------------------------------------------------
   Seam backdrop — carry the previous section's ground under the tear
   ---------------------------------------------------------------------
   The seam SVG paints its own section's colour as a wave and leaves the
   space above the wave transparent. That strip lives in a reserved margin,
   so it shows the PAGE ground — correct only when the section above is also
   the page ground. Following the beige stamps band it rendered a cream
   sliver, so a single torn edge read as two.

   Resolved here rather than as a per-section colour setting because the
   section order is editable: whatever ends up above a seam should supply
   its backdrop, without anyone remembering to update a swatch.
   --------------------------------------------------------------------- */
(function () {
  if (!document.body || !document.body.classList.contains('pdp-v2')) return;

  var seams = document.querySelectorAll('.pdpSeam');
  if (!seams.length) return;

  var isPaintable = function (c) {
    return c && c !== 'transparent' && !/^rgba\(0,\s*0,\s*0,\s*0\)$/.test(c);
  };

  /* Walk sections in DOCUMENT order, not by previousElementSibling.
     Sibling walking broke on the footer: it lives under <body> rather than
     <main>, and the element before it is an app-embed div, so the search
     wandered into unrelated markup and came back green — which painted the
     footer's own colour above its wave and flattened the tear into a hard
     edge. querySelectorAll returns document order across the whole page,
     which is the order the reader actually sees. */
  var apply = function () {
    var sections = document.querySelectorAll('.shopify-section');

    /* what a transparent section actually shows */
    var pageGround = (function () {
      var b = getComputedStyle(document.body).backgroundColor;
      return isPaintable(b) ? b : '';
    }());

    var groundOf = function (el) {
      /* sections paint on an inner <section>; the wrapper is transparent */
      var inner = el.querySelector(':scope > section, :scope > div');
      if (inner) {
        var ci = getComputedStyle(inner).backgroundColor;
        if (isPaintable(ci)) return ci;
      }
      var c = getComputedStyle(el).backgroundColor;
      return isPaintable(c) ? c : '';
    };

    for (var i = 0; i < seams.length; i++) {
      var seam = seams[i];
      var host = seam.closest('.shopify-section');
      if (!host) continue;

      var idx = Array.prototype.indexOf.call(sections, host);
      if (idx < 1) continue;                    /* nothing above it to match */

      /* The FIRST RENDERED section above decides, full stop — and if it
         paints nothing, the answer is the page ground, not some earlier
         section's colour.

         This used to keep walking until it found a section that painted,
         treating transparent as "no ground to inherit". But transparent is
         not absent: a transparent section shows the page ground, and that is
         exactly what the reader sees in the strip above the seam. So the FAQ
         and footer seams walked past the stamps band and the recommended
         carousel (both transparent, both reading #F5F3E7) and inherited
         #EFEBDE from the ingredient section further up — a beige band between
         two cream ones. Only empty/disabled sections are skipped now. */
      var colour = '';
      for (var j = idx - 1; j >= 0; j--) {
        if (sections[j].getBoundingClientRect().height <= 0) continue;
        colour = groundOf(sections[j]) || pageGround;
        break;
      }

      if (colour) seam.style.setProperty('--seam-above', colour);
      else seam.style.removeProperty('--seam-above');
    }
  };

  apply();
  /* section reorder / re-render in the theme editor */
  document.addEventListener('shopify:section:load', apply);
  document.addEventListener('shopify:section:reorder', apply);
}());


/* ---------------------------------------------------------------------
   Ambient loops — pause what nobody is watching
   ---------------------------------------------------------------------
   Anything marked [data-loop] runs an infinite CSS animation. Chrome does
   not reliably stop a composited transform just because it has scrolled
   out of view, and it certainly does not stop for a backgrounded tab on
   every platform. Both are pure waste: the marquee is decorative, so if
   it cannot be seen it should not be running.

   Toggling a class rather than touching style directly keeps the pause
   in CSS, where the hover and focus-within pauses already live — they
   compose instead of fighting over the same inline property.
   --------------------------------------------------------------------- */
(function () {
  var loops = document.querySelectorAll('[data-loop]');
  if (!loops.length) return;

  var offscreen = [];   /* mirrors each loop's visibility */

  var sync = function () {
    var hidden = document.hidden;
    for (var i = 0; i < loops.length; i++) {
      loops[i].classList.toggle('is-paused', hidden || offscreen[i] === true);
    }
  };

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var idx = Array.prototype.indexOf.call(loops, e.target);
        if (idx > -1) offscreen[idx] = !e.isIntersecting;
      });
      sync();
    }, { rootMargin: '120px 0px' });   /* resume just before it is reached */
    for (var k = 0; k < loops.length; k++) io.observe(loops[k]);
  }

  document.addEventListener('visibilitychange', sync);
  sync();
}());


/* ---------------------------------------------------------------------
   Reading thread  (ported from hibiscus-final/index.html `runThread`)
   ---------------------------------------------------------------------
   NOT an IntersectionObserver. The spec drives this off continuous scroll
   position, and that difference is the whole effect: an observer fires
   once when a row crosses a line, so the checks flip on like switches.
   This computes a 0..1 "read" progress for the list as a whole, publishes
   it as --read, and lights each item as the growing thread passes it. The
   line fills WITH you rather than finishing the moment the list arrives.

   Their comment, kept because it is the design intent:
       "wide range: the thread should track reading, not finish on arrival"

   start 0.92vh -> end 0.30vh is deliberately generous: the fill begins
   while the list is still low on screen and completes as it reaches the
   comfortable reading band, so it tracks eyes rather than the viewport.
   --------------------------------------------------------------------- */
(function () {
  var groups = [];
  document.querySelectorAll('[data-thread]').forEach(function (el) {
    var items = el.querySelectorAll('[data-lit-item]');
    if (items.length) groups.push({ el: el, items: items });
  });
  if (!groups.length) return;

  var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (still) {
    groups.forEach(function (g) {
      g.el.style.setProperty('--read', '1');
      g.items.forEach(function (li) { li.dataset.lit = '1'; });
    });
    return;
  }

  function runThread(t) {
    var b = t.el.getBoundingClientRect();
    if (b.bottom < -100 || b.top > innerHeight + 100) return;
    var start = innerHeight * 0.92, end = innerHeight * 0.30;
    var k = Math.max(0, Math.min(1, (start - b.top) / (b.height + (start - end))));
    t.el.style.setProperty('--read', k.toFixed(3));
    var lit = k * b.height;
    t.items.forEach(function (li) {
      li.dataset.lit = ((li.getBoundingClientRect().top - b.top) <= lit + 8) ? '1' : '';
    });
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      groups.forEach(runThread);
      ticking = false;
    });
  }

  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll, { passive: true });
  onScroll();

  /* If the compositor never runs (throttled tab, or the list is already
     past), the marks must not be left empty. */
  setTimeout(function () {
    groups.forEach(function (g) {
      var b = g.el.getBoundingClientRect();
      if (b.top < innerHeight && b.bottom > 0) return;   // on screen: let scroll drive it
      if (b.bottom < 0) {                                 // scrolled past: complete it
        g.el.style.setProperty('--read', '1');
        g.items.forEach(function (li) { li.dataset.lit = '1'; });
      }
    });
  }, 4000);
}());
